'use client';
import React, { useEffect, useRef } from 'react';

const bullets = [
  {
    title: 'Atenție la detalii',
    desc: 'Nu ne grăbim. Alocăm timpul necesar fiecărui element, de la cele mai fine crăpături din interior până la corecția lacului.',
    icon: (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <circle cx="11" cy="11" r="8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M21 21l-4.35-4.35" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: 'Echipamente profesionale',
    desc: 'Folosim doar soluții dedicate și tehnologie de ultimă oră (igienizare cu ozon, aparate de extracție, polishare în mai mulți pași).',
    icon: (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: 'Garanția calității',
    desc: 'Fiecare mașină care iese din atelierul nostru trebuie să fie impecabilă. Dacă nu ești mulțumit, rectificăm pe loc.',
    icon: (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: 'Transparență',
    desc: 'Îți explicăm exact ce procese aplicăm și ce rezultate putem obține, fără promisiuni nerealiste.',
    icon: (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
];

export default function DespreNoiSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) obs?.observe(sectionRef?.current);
    return () => obs?.disconnect();
  }, []);

  return (
    <section
      id="despre-noi"
      ref={sectionRef}
      className="py-24 md:py-32"
      style={{ background: 'var(--cockpit-black)' }}
    >
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="metric-pill mb-5 reveal">Despre Noi</div>
          <h2
            className="font-manrope font-extrabold text-white reveal reveal-delay-1"
            style={{ fontSize: 'clamp(28px, 4.5vw, 52px)', letterSpacing: '-0.03em' }}
          >
            Asa Detailing Botoșani
          </h2>
          <p
            className="mt-6 max-w-2xl mx-auto reveal reveal-delay-2"
            style={{ color: 'var(--aluminum-dim)', fontSize: '16px', lineHeight: '1.75' }}
          >
            Am început din pasiune pentru mașini și din dorința de a oferi în Botoșani un nivel de îngrijire auto care merge dincolo de o simplă spălare. La Asa Detailing nu doar curățăm mașini, ci le conservăm valoarea și aspectul estetic.
          </p>
        </div>

        {/* De ce să ne alegi */}
        <div className="mb-12">
          <h3
            className="font-manrope font-bold text-white text-center mb-8 reveal reveal-delay-2"
            style={{ fontSize: 'clamp(18px, 2.5vw, 24px)', letterSpacing: '-0.02em' }}
          >
            De ce să ne alegi pe noi?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {bullets?.map((bullet, i) => (
              <div
                key={bullet?.title}
                className={`reveal reveal-delay-${i + 2} rounded-2xl p-6 flex gap-4`}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(63,63,70,0.6)',
                }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                  style={{
                    background: 'rgba(59,130,246,0.12)',
                    border: '1px solid rgba(59,130,246,0.25)',
                    color: 'var(--ceramic-blue)',
                  }}
                >
                  {bullet?.icon}
                </div>
                <div>
                  <div
                    className="font-manrope font-bold text-white mb-2"
                    style={{ fontSize: '15px', letterSpacing: '-0.01em' }}
                  >
                    {bullet?.title}
                  </div>
                  <p style={{ fontSize: '14px', color: 'rgba(212,212,216,0.8)', fontFamily: 'DM Sans, sans-serif', lineHeight: '1.6' }}>
                    {bullet?.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA banner */}
        <div
          className="reveal reveal-delay-4 rounded-2xl p-8 text-center"
          style={{
            background: 'rgba(59,130,246,0.06)',
            border: '1px solid rgba(59,130,246,0.25)',
          }}
        >
          <p
            className="font-manrope font-semibold text-white mb-5"
            style={{ fontSize: 'clamp(15px, 2vw, 18px)', lineHeight: '1.6' }}
          >
            Mașina ta merită cea mai bună îngrijire. Te așteptăm la atelierul nostru din Botoșani pentru o evaluare gratuită a stării mașinii tale!
          </p>
          <a
            href="https://revmy.ro/service-provider-profile/botosani/detailing/asa-detailing"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-block"
            style={{ fontSize: '15px', padding: '12px 28px' }}
          >
            Rezervă o evaluare gratuită
          </a>
        </div>
      </div>
    </section>
  );
}
